import React, { useEffect, useId } from 'react';
import useBrandingStore from '../store/brandingStore';

/**
 * Brand mark: a rounded badge with a stylised "M" (open-book pages) and an
 * amber progress-bar accent underneath, tying back to the achievement/
 * progress motif used elsewhere (BrandPanel, StatCards). Used in the
 * Sidebar, the auth BrandPanel, and the mobile auth header.
 *
 * The gradient id must be unique per instance: when more than one LogoMark
 * renders on the same page (e.g. the desktop BrandPanel and the mobile-only
 * header both exist in the DOM on Login/Register, one just hidden via CSS),
 * duplicate SVG ids resolve to whichever comes first in the DOM — if that
 * one sits inside a `display:none` subtree, every fill="url(#...)" that
 * points at it silently renders as transparent.
 */
export function LogoMark({ size = 36, rounded = true }) {
  const gradientId = useId();
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="100" y2="100" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="rgb(var(--color-primary-500, 125 90 239))" />
          <stop offset="100%" stopColor="rgb(var(--color-primary-800, 70 37 158))" />
        </linearGradient>
      </defs>
      <rect width="100" height="100" rx={rounded ? 24 : 0} fill={`url(#${gradientId})`} />
      <path
        d="M22,72 L37,28 L50,56 L63,28 L78,72"
        stroke="#ffffff"
        strokeWidth="9"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <rect x="39" y="80" width="22" height="6" rx="3" fill="#f7930d" />
    </svg>
  );
}

export default function Logo({ size = 36, showWordmark = true, wordmarkClassName = '', textColor = 'text-white' }) {
  const { siteName, logo, load } = useBrandingStore();
  useEffect(() => { load(); }, [load]);

  return (
    <div className="flex items-center gap-2.5">
      {logo ? (
        <img src={logo} alt="" width={size} height={size} className="object-contain shrink-0" style={{ width: size, height: size }} />
      ) : (
        <LogoMark size={size} />
      )}
      {showWordmark && (
        <span className={`font-display font-bold tracking-tight ${textColor} ${wordmarkClassName}`}>
          {siteName || "L'mcid"}
        </span>
      )}
    </div>
  );
}
